import { isNotNullish, isNullish } from '@nx/stdlib';

import type {
  CreateIpFilterBody,
  CreateNamespaceIpFilterRequest,
  IpFilterDetailsResponse,
  IpFilterParam,
  IpFilterResponse,
  UpdateIpFilterRequest,
} from '../api-types/ip-filters';
// oxlint-disable-next-line import/no-cycle
import consoleApi from '../console.api';
import type { IpFilter, IpFilterDetails } from '../console.types';
import { transformIpFilterResponse, transformIpFiltersDetailsResponse } from '../transformers/ip-filters';
import { type Builder, LIST_ID } from './types';

export const ipFiltersEndpoints = (builder: Builder) => ({
  // /ip-filter/:id
  getIpFilter: builder.query<IpFilter, string>({
    query: (id) => `ip-filters/${id}`,
    transformResponse: (ipFilter: IpFilterResponse) => transformIpFilterResponse(ipFilter),
    providesTags: (result, error, id) => [{ type: 'IpFilter', id }],
  }),
  updateIpFilter: builder.mutation<IpFilter, UpdateIpFilterRequest>({
    query: ({ id, ...updateBody }) => ({
      url: `ip-filters/${id}`,
      method: 'PATCH',
      body: updateBody,
    }),
    transformResponse: (ipFilter: IpFilterResponse) => transformIpFilterResponse(ipFilter),
    invalidatesTags: (result, error, arg) => [
      { type: 'IpFilter', id: result?.id },
      { type: 'IpFilter', id: LIST_ID },
      { type: 'IpFilterDetails', id: result?.id },
      { type: 'Organization' },
      { type: 'OrganizationProject' },
      { type: 'Project' },
    ],
  }),
  deleteIpFilter: builder.mutation<IpFilter, IpFilterParam>({
    query: ({ id }) => ({ url: `ip-filters/${id}`, method: 'DELETE' }),
    invalidatesTags: (result, error, { id }) => [
      { type: 'IpFilter', id },
      { type: 'IpFilter', id: LIST_ID },
      { type: 'Organization' },
      { type: 'OrganizationProject' },
      { type: 'Project' },
    ],
  }),
  // /ip-filters
  listIpFiltersByOrganization: builder.query<IpFilter[], string>({
    query: (organizationId) => ({
      url: `/ip-filters`,
      params: {
        OrganizationId: organizationId,
      },
      method: 'GET',
    }),
    transformResponse: (ipFilters: IpFilterResponse[]) =>
      ipFilters.map((ipFilter) => transformIpFilterResponse(ipFilter)),
    providesTags: (result, error, organizationId) => {
      if (isNotNullish(result)) {
        return [...result.map(({ id }) => ({ type: 'IpFilter' as const, id })), { type: 'IpFilter', id: LIST_ID }];
      }
      return [{ type: 'IpFilter', id: LIST_ID }];
    },
  }),
  createIpFilter: builder.mutation<IpFilter, CreateIpFilterBody>({
    query: (createBody) => ({
      url: '/ip-filters',
      method: 'POST',
      body: createBody,
    }),
    onQueryStarted: async ({ Name, OrganizationId, AllowList, Description }, { dispatch, queryFulfilled }) => {
      const updateCache = dispatch(
        consoleApi.util.updateQueryData('listIpFiltersByOrganization', OrganizationId, (draft) => {
          draft.unshift({
            name: Name,
            description: Description,
            allowList: AllowList.map((ip) => {
              const [address, prefixLen] = ip.IpRange.split('/');
              if (isNullish(address) || isNullish(prefixLen) || isNaN(parseInt(prefixLen, 10))) {
                return { address: '', prefixLen: 0 };
              }

              return { address, prefixLen: parseInt(prefixLen, 10) };
            }),
            filteredEntities: {},
            id: '',
            organizationId: OrganizationId,
          });
        }),
      );
      try {
        await queryFulfilled;
      } catch (e) {
        updateCache.undo();
      }
    },
    transformResponse: (ipFilter: IpFilterResponse) => transformIpFilterResponse(ipFilter),
    invalidatesTags: (result, error, arg) => [
      { type: 'IpFilter', id: result?.id },
      { type: 'IpFilter', id: LIST_ID },
      { type: 'IpFilterDetails', id: result?.id },
      { type: 'Organization' },
      { type: 'OrganizationProject' },
      { type: 'Project' },
    ],
  }),
  // /databases/:dbId/ip-filters
  getIpFiltersByInstance: builder.query<IpFilterDetails, string>({
    query: (id) => `databases/${id}/ip-filters`,
    transformResponse: (ipFilter: IpFilterDetailsResponse) => transformIpFiltersDetailsResponse(ipFilter),
    providesTags: (result, error, id) => [{ type: 'IpFilterDetails', id }],
  }),
  // /namespaces/:projectId/ip-filters
  listIpFiltersByProject: builder.query<IpFilter[], string>({
    query: (projectId) => `namespaces/${projectId}/ip-filters`,
    transformResponse: (ipFilters: IpFilterResponse[]) =>
      ipFilters.map((ipFilter) => transformIpFilterResponse(ipFilter)),
    providesTags: (result, error, projectId) => {
      if (isNotNullish(result)) {
        return [...result.map(({ id }) => ({ type: 'IpFilter' as const, id })), { type: 'IpFilter', id: LIST_ID }];
      }
      return [{ type: 'IpFilter', id: LIST_ID }];
    },
  }),
  createNamespaceIpFilter: builder.mutation<IpFilter, CreateNamespaceIpFilterRequest>({
    query: ({ projectId, ...createBody }) => ({
      url: `/namespaces/${projectId}/ip-filters`,
      method: 'POST',
      body: createBody,
    }),
    transformResponse: (ipFilter: IpFilterResponse) => transformIpFilterResponse(ipFilter),
    invalidatesTags: (result) => [
      { type: 'IpFilter', id: result?.id },
      { type: 'IpFilter', id: LIST_ID },
      { type: 'IpFilterDetails', id: result?.id },
      { type: 'Organization' },
      { type: 'OrganizationProject' },
      { type: 'Project' },
    ],
  }),
});
